"use client";

import { useEffect, useRef, useState } from "react";

/* Conversa real de CS acontecendo ao vivo: a especialista Shoppub
   analisa, sugere, o cliente aprova e a ação é executada — mensagem
   a mensagem, com indicador de digitação. Loop autônomo. */

type Msg = { de: "cs" | "cliente"; texto: string; hora: string };

const ROTEIRO: Msg[] = [
  {
    de: "cs",
    texto:
      "Bom dia, Marina! Fechei a análise da sua semana: conversão subiu para 3,8% e o ticket médio está em R$ 289.",
    hora: "09:02",
  },
  {
    de: "cs",
    texto:
      "Minha sugestão para o fim de semana: ativar o desconto progressivo a partir de 3 peças e um push para quem abandonou carrinho.",
    hora: "09:03",
  },
  {
    de: "cliente",
    texto: "Ótima leitura! Pode ativar os dois. Conseguimos revisar as campanhas do app também?",
    hora: "09:05",
  },
  {
    de: "cs",
    texto:
      "Ativado ✓ E agendei nossa revisão de campanhas para amanhã às 10h — te mando o comparativo no fim do dia.",
    hora: "09:06",
  },
];

// [mensagens visíveis, quem está digitando]
type Estado = { visiveis: number; digitando: "cs" | "cliente" | null };

export default function MockCs() {
  const raiz = useRef<HTMLDivElement>(null);
  const janela = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  const [estado, setEstado] = useState<Estado>({ visiveis: 0, digitando: null });

  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight * 0.95) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setOn(true), io.disconnect()),
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!on) return;
    let vivo = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(() => vivo && fn(), ms));

    const rodada = () => {
      setEstado({ visiveis: 0, digitando: null });
      let clock = 700;
      ROTEIRO.forEach((m, i) => {
        const digita = m.de === "cs" ? 1600 : 1200;
        t(() => setEstado({ visiveis: i, digitando: m.de }), clock);
        clock += digita;
        t(() => setEstado({ visiveis: i + 1, digitando: null }), clock);
        clock += 900;
      });
      t(rodada, clock + 3800);
    };
    rodada();
    return () => {
      vivo = false;
      timers.forEach(clearTimeout);
    };
  }, [on]);

  useEffect(() => {
    const el = janela.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [estado]);

  return (
    <div ref={raiz} className="select-none rounded-card border border-white/10 bg-ink-soft p-6">
      {/* cabeçalho da equipe de CS */}
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-lime font-display text-xs font-bold text-ink">
            CS
          </span>
          <div>
            <div className="text-sm font-bold text-paper">Ana · CS Shoppub</div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-lime">
              <span className="h-1.5 w-1.5 rounded-full bg-lime" />
              online agora
            </div>
          </div>
        </div>
        <span className="rounded-full border border-lime/40 bg-lime/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-lime">
          Especialista dedicada
        </span>
      </div>

      {/* conversa em tempo real — altura fixa, scroll interno */}
      <div ref={janela} className="chat-scroll mt-4 h-[300px] overflow-y-auto overscroll-contain pr-2">
        <div className="flex min-h-full flex-col justify-end gap-2.5">
        {ROTEIRO.slice(0, estado.visiveis).map((m, i) => (
          <div
            key={i}
            className={`animate-[reveal-up_0.4s_cubic-bezier(0.22,1,0.36,1)] ${
              m.de === "cliente" ? "self-end" : ""
            }`}
          >
            <div
              className={`max-w-[88%] rounded-2xl p-3.5 text-[13px] leading-relaxed ${
                m.de === "cs"
                  ? "rounded-tl-md bg-white/8 text-paper/85"
                  : "ml-auto rounded-tr-md bg-lime font-medium text-ink"
              }`}
            >
              {m.texto}
            </div>
            <div
              className={`mt-1 text-[10px] text-smoke-dark ${
                m.de === "cliente" ? "text-right" : ""
              }`}
            >
              {m.de === "cs" ? `Ana · ${m.hora}` : `Você · ${m.hora} ✓✓`}
            </div>
          </div>
        ))}

        {/* indicador de digitação */}
        {estado.digitando && (
          <div
            className={`flex w-fit items-center gap-1.5 rounded-2xl px-4 py-3 ${
              estado.digitando === "cs"
                ? "rounded-tl-md bg-white/8"
                : "self-end rounded-tr-md bg-lime/25"
            }`}
          >
            {[0, 1, 2].map((d) => (
              <span
                key={d}
                className="h-1.5 w-1.5 animate-pulse rounded-full bg-paper/60"
                style={{ animationDelay: `${d * 0.2}s`, animationDuration: "0.9s" }}
              />
            ))}
          </div>
        )}
        </div>
      </div>

      <p className="mt-4 border-t border-white/10 pt-3.5 text-[12px] leading-relaxed text-smoke-dark">
        Sua loja com uma <b className="text-paper">equipe de Customer Success dedicada</b> —
        acompanhando números, sugerindo ações e executando junto, toda semana.
      </p>
    </div>
  );
}
